import {
  Complementary,
  GetProps,
  ProductDetails,
  Similar,
  SubSDK,
  Tags,
} from "../types";
import { GetProductByIdQuery, ProductDetailsFragment } from "./queries/product";
import { GraphqlClient } from "@/utils/graphql";
import { transformShopifyProduct } from "./utils";
import { ShopifyProductData } from "./utils";
import { CDN_URL } from "@/constants/cdn";
export interface HeliumInfo {
  apiKey: string;
  version: 1 | 2;
  shopify: {
    shop: string;
    storefrontAccessToken: string;
  };
}
interface ShopifyProduct {
  tags: Tags;
  similar: Similar;
  complementary?: Complementary;
  title: string;
  description: string;
  handle: string;
  featuredImage: {
    src: string;
    width: number;
    height: number;
    altText: string;
  };
}
export interface HeliumProduct {
  title: string;
  description: string;
  handle: string;
  featuredImage: {
    src: string;
    width: number;
    height: number;
    altText: string;
  };
}
const apiUrl = CDN_URL;

export class HeliShop implements SubSDK<HeliumInfo> {
  info: HeliumInfo;
  private fetcher: <T = any>(path: string, options?: RequestInit) => Promise<T>;
  private shopifyFetcher: GraphqlClient;
  private handleBatchQueue: Map<
    string,
    {
      resolve: (value: ProductDetails | undefined) => void;
      reject: (reason?: unknown) => void;
    }
  > = new Map();
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly batchDelay: number = 20;
  constructor(props: HeliumInfo) {
    window.heliumBatch = this;
    this.info = props;

    this.fetcher = async (path: string, options?: RequestInit) => {
      const orgId = window.helium.brandData.orgId;
      const [, type, handle] = path.split("/");
      const storeId = window.helium.brandData.storeId;
      let useStoreId = true;
      if (
        type === "similar" ||
        type === "complementary" ||
        type === "ui-description" ||
        type === "tag_recommendation"
      ) {
        useStoreId = true && storeId;
        if (typeof window.helium.brandData.useStoreId === "boolean") {
          useStoreId = window.helium.brandData.useStoreId;
        }
      }
      if (type === "tags" || type === "details") {
        useStoreId = false;
      }
      if (type === "products") {
        useStoreId = true;
        const res = await fetch(
          apiUrl +
            `/${orgId}/products/${type}.${useStoreId ? `${storeId}.` : ""}json`,
          {
            //...options,
            //headers: { "x-helium-api-key": this.info.apiKey },
          },
        );
        return res.json();
      }
      const url =
        apiUrl +
        `/${orgId}/products/${handle}/${type}.${useStoreId ? `${storeId}.` : ""}json`;
      const res = await fetch(url, {
        //...options,
        //headers: { "x-helium-api-key": this.info.apiKey },
      });
      return res.json();
    };

    this.shopifyFetcher = new GraphqlClient({
      shop: props.shopify.shop,
      storefrontAccessToken: props.shopify.storefrontAccessToken,
    });
    this.batchProcessor = this.batchProcessor.bind(this);
  }
  async getProductByHandle(props: { handle: string }): Promise<undefined> {}

  async getTags({ id }: GetProps) {
    return (await this.fetcher("/tags/" + id)) || {};
  }

  async getSimilar({ id }: GetProps) {
    return await this.fetcher("/similar/" + id);
  }
  async getFaq({ id }: GetProps) {
    const orgId = window.helium.brandData.orgId;
    const storeId = window.helium.brandData.storeId;
    const path = `${apiUrl}/${orgId || storeId}/products/${id}/faq.json`;
    // const res = await fetch(`${apiUrl}/api/v1/faq/${id}.`, {
    //   headers: { "x-helium-api-key": this.info.apiKey },
    // });
    // return res.json();
    const res = await fetch(path).then((r) => r.text());
    return JSON.parse(res);
  }

  async getComplementary({ id }: GetProps) {
    return (await this.fetcher("/complementary/" + id)) || [];
  }
  async getProducts() {
    return (await this.fetcher("/products")) || [];
  }

  async getProductUiDescription({ id }: GetProps) {
    return (await this.fetcher("/ui-description/" + id)) || {};
  }
  async getProductTagRecommendation({ id }: GetProps) {
    try {
      return (await this.fetcher("/tag_recommendation/" + id)) || {};
    } catch (error) {
      console.error(
        `Error fetching tag recommendations for product ${id}:`,
        error,
      );
      return null;
    }
  }
  promiseMap = new Map<string, Promise<ProductDetails | null>>();
  async getProductDetails({
    id: handle,
  }: GetProps): Promise<ProductDetails | null> {
    if (!handle || typeof handle !== "string") {
      console.error("getProductDetails called with invalid handle:", handle);
      return Promise.reject(
        new Error("Invalid handle provided to getProductDetails"),
      );
    }
    const currentItem = this.promiseMap.get(handle);
    if (currentItem) {
      return await currentItem;
    }
    const promise = new Promise<ProductDetails>((resolve, reject) => {
      this.handleBatchQueue.set(handle, { resolve, reject });

      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
      }

      this.batchTimeout = setTimeout(this.batchProcessor, this.batchDelay);
    }).catch(() => null);
    this.promiseMap.set(handle, promise);
    return await promise;
  }
  private async batchProcessor(): Promise<void> {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    const currentBatch = new Map(this.handleBatchQueue);
    this.handleBatchQueue.clear();

    if (currentBatch.size === 0) {
      console.log("Batch processor called with empty queue.");
      return; // Nothing to process
    }

    const handlesToFetch = Array.from(currentBatch.keys());

    let query = `query GetProductsByHandles {\n`;
    const variables = {};
    const handleToAliasMap = new Map<string, string>();

    handlesToFetch.forEach((handle, index) => {
      const alias = `h_${handle.replace(/[^a-zA-Z0-9_]/g, "_")}_${index}`;
      handleToAliasMap.set(handle, alias);

      query += `  ${alias}: product(handle: "${handle}") { ...ProductDetailsFragment }\n`;
    });

    query += `}\n${ProductDetailsFragment}`;

    try {
      const data = await this.shopifyFetcher.request<{
        [key: string]: ShopifyProductData | null;
      }>(query, variables);

      handlesToFetch.forEach((handle) => {
        const alias = handleToAliasMap.get(handle);
        const promiseCallbacks = currentBatch.get(handle);
        if (!promiseCallbacks) {
          console.warn(
            `Internal Error: Callbacks not found for handle ${handle} in batch results.`,
          );
          return;
        }

        if (!alias) {
          console.warn(`Internal Error: Alias not found for handle ${handle}.`);

          promiseCallbacks.resolve(undefined);
          return;
        }

        const productNode = data[alias];

        if (productNode) {
          try {
            const transformedProduct = transformShopifyProduct(productNode);
            const inStock = transformedProduct.variants.some(
              (e) => e.availableForSale,
            );

            if (inStock) {
              promiseCallbacks.resolve(transformedProduct);
            } else {
              promiseCallbacks.resolve(undefined);
            }
          } catch (transformError) {
            console.error(
              `Error transforming product data for handle ${handle} (Alias: ${alias}):`,
              transformError,
            );
            promiseCallbacks.resolve(undefined);
          }
        } else {
          console.warn(
            `Product data not found for handle ${handle} (Alias: ${alias}) in batch response.`,
          );
          promiseCallbacks.resolve(undefined);
        }
      });
    } catch (error) {
      console.error(
        `Error fetching or processing batched product details by handle for ${handlesToFetch.length} items:`,
        error,
      );
      currentBatch.forEach(({ resolve, reject }, handle) => {
        reject(
          new Error(
            `Failed to fetch batch including handle ${handle}: ${error instanceof Error ? error.message : String(error)}`,
          ),
        );
      });
    }
  }
}
